import React from 'react';

/**
 * /Machine_Config_Translator
 *
 * Storefront for the Windows desktop translator EXE. Static page; the
 * actual translation work happens in the downloaded app.
 */

const APP_VERSION = '1.1';
const DICTIONARY_TERMS = 542;
const EXE_SIZE_MB = 26;
const DICTIONARY_UPDATED = '2026-05-09';

const EXE_HREF = '/Machine_Config_Translator/MachineConfigTranslator.exe';
const DICT_HREF = '/Machine_Config_Translator/translations.json';
const SCREENSHOTS = {
    chinese: '/Machine_Config_Translator/screenshots/01-plc-management-chinese.png',
    mixed: '/Machine_Config_Translator/screenshots/02-plc-management-mixed.png',
    english: '/Machine_Config_Translator/screenshots/03-plc-management-english.png',
};

const SAFE_ATTRS = [
    'Desc', 'Name', 'ParamName', 'Group',
    'UserDes', 'SysDes', 'ModuleDes', 'ExtIODesc',
    'FormTitle', 'ModuleName', 'DeviceName',
    '<Warning>', '<Alarm>', 'KeyDesc13…44',
];

const FILE_TYPES: Array<[string, string]> = [
    ['.cfgpkg', 'TubePro machine config package (zip container)'],
    ['.stps', 'PLC step / sequence definitions'],
    ['.Repo', 'PLC program catalog and repository'],
    ['.vars', 'PLC variable tables'],
    ['.lxm', 'Tube template / weld definitions'],
    ['.config', 'Machine, IO, units, alarms, and HMI configs'],
];

export const MachineConfigTranslatorPage: React.FC = () => {
    return (
        <div style={s.page}>
            <div style={s.container}>
                {/* Top bar with home link */}
                <div style={s.topBar}>
                    <a href="/" style={s.backLink}>← XOVR/tools</a>
                </div>

                {/* Hero */}
                <header style={s.hero}>
                    <div style={s.heroLeft}>
                        <div style={s.eyebrow}>For TubePro / CypCut laser cutters</div>
                        <h1 style={s.title}>Machine Config Translator</h1>
                        <p style={s.lede}>
                            Convert Chinese PLC programs and HMI labels in your{' '}
                            <code style={s.code}>.cfgpkg</code> machine config to clean,
                            operator-friendly English &mdash; without touching a single operational parameter.
                        </p>

                        <div style={s.ctaRow}>
                            <a href={EXE_HREF} download style={s.primaryBtn}>
                                <span style={{ marginRight: 8 }}>↓</span>
                                Download for Windows ({EXE_SIZE_MB} MB)
                            </a>
                            <a href={DICT_HREF} download style={s.secondaryBtn}>
                                Dictionary ({DICTIONARY_TERMS} terms)
                            </a>
                        </div>

                        <div style={s.metaRow}>
                            v{APP_VERSION} &middot; Windows 10 / 11 &middot; Standalone EXE,
                            no installer required &middot; Dictionary updated {DICTIONARY_UPDATED}
                        </div>
                    </div>

                    <div style={s.heroRight}>
                        <div style={s.heroFrame}>
                            <img
                                src={SCREENSHOTS.english}
                                alt="PLC Management screen after translation, fully in English"
                                style={s.heroImg}
                            />
                        </div>
                        <div style={s.caption}>After: same machine, every program label in English.</div>
                    </div>
                </header>

                {/* Why */}
                <Section title="Why use it">
                    <div style={s.grid4}>
                        <ValueCard
                            head="Operator-safe by design"
                            body="Only display text changes. The translator hard-blocks any edit to ports, timing, speeds, coordinates, or any non-whitelisted attribute."
                        />
                        <ValueCard
                            head="Works on the original .cfgpkg"
                            body="Open the file the machine already uses. The app extracts, translates, repacks. No re-authoring."
                        />
                        <ValueCard
                            head="542-term technical dictionary"
                            body="Built from real TubePro / CypCut programs across multiple machines. Piercing stages, chuck operations, bracket coupling, unloading flows."
                        />
                        <ValueCard
                            head="Offline. Yours. Free."
                            body="Files never leave the workshop PC. Optional DeepL fallback only runs if you provide your own key."
                        />
                    </div>
                </Section>

                {/* Before / After */}
                <Section title="Before / after" subtitle="The same PLC Management screen, translated in place. Every program name, parameter, and step description is replaced &mdash; nothing else.">
                    <div style={s.grid3}>
                        <Shot label="BEFORE" src={SCREENSHOTS.chinese} caption="Original machine config, fully in Chinese." />
                        <Shot label="DURING" src={SCREENSHOTS.mixed} caption="Mid-translation: dictionary catches standard terms first." />
                        <Shot label="AFTER" src={SCREENSHOTS.english} caption="Operator-ready English, identical structure." />
                    </div>
                </Section>

                {/* Install */}
                <Section title="Install" subtitle="No installer, no admin rights, no telemetry. Download, double-click, done.">
                    <div style={s.grid3}>
                        <Step n={1} head="Download the EXE">
                            Save <a href={EXE_HREF} style={s.inlineLink} download>MachineConfigTranslator.exe</a>{' '}
                            ({EXE_SIZE_MB} MB) to a folder you control &mdash; for example{' '}
                            <code style={s.code}>C:\XOVR\</code>.
                        </Step>
                        <Step n={2} head="Place the dictionary alongside it">
                            Put <a href={DICT_HREF} style={s.inlineLink} download>translations.json</a>{' '}
                            in the same folder as the EXE. The bundled copy is used as a fallback;
                            a sibling file always wins.
                        </Step>
                        <Step n={3} head="Run it">
                            Double-click. Windows SmartScreen may warn the first time &mdash; click{' '}
                            <em>More info</em> &rarr; <em>Run anyway</em>. The app does not need internet.
                        </Step>
                    </div>
                </Section>

                {/* Usage */}
                <Section title="Translate a machine config" subtitle="The typical flow with a backup .cfgpkg exported from TubePro.">
                    <div style={s.grid2}>
                        <Card head="1. Open the package">
                            File &rarr; Open Package, pick the <code style={s.code}>.cfgpkg</code>.
                            The app extracts it to a working folder and lists every translatable file
                            (.stps, .config, .lxm, .Repo, .vars).
                        </Card>
                        <Card head="2. Preview">
                            Hit <em>Preview</em> on any file to see a side-by-side diff. Every change
                            is colored: green for safe display text, red if anything outside the whitelist
                            would be touched (the engine refuses to save those).
                        </Card>
                        <Card head="3. Translate all">
                            Click <em>Translate All</em>. Originals are copied to{' '}
                            <code style={s.code}>BACKUP/</code> automatically. Files with unknown Chinese
                            terms are listed in the &ldquo;Untranslated&rdquo; report.
                        </Card>
                        <Card head="4. Repack">
                            File &rarr; Save as Package writes a new <code style={s.code}>.cfgpkg</code>{' '}
                            next to the original. Load it on the machine the same way you loaded the Chinese version.
                        </Card>
                    </div>
                </Section>

                {/* Safety */}
                <Section title="What it changes (and what it never touches)">
                    <div style={s.grid2}>
                        <div>
                            <p style={s.body}>
                                The engine has a hardcoded safety whitelist. If a translation would change
                                anything outside it &mdash; or change a number, a port, or a special character &mdash;
                                the file is rejected and nothing is written.
                            </p>
                            <ul style={s.bullets}>
                                <li>Line count of every file is preserved exactly.</li>
                                <li>Every numeric value (timing, speed, port, coordinate) is preserved bit-for-bit.</li>
                                <li>Special characters (<code style={s.code}>= | &amp; ; : [ ] {'{ }'} \</code>) cannot appear or disappear during translation.</li>
                                <li>A pre-write verifier diffs the original and the translated copy line by line, attribute by attribute, and aborts on any change outside the whitelist.</li>
                            </ul>
                        </div>
                        <div style={s.whitelistBox}>
                            <div style={s.whitelistHead}>Whitelist (read-only)</div>
                            <div style={s.whitelistSub}>The only attributes and tags the translator may modify.</div>
                            <div style={s.whitelistGrid}>
                                {SAFE_ATTRS.map((a) => (
                                    <code key={a} style={s.whitelistChip}>{a}</code>
                                ))}
                            </div>
                        </div>
                    </div>
                </Section>

                {/* File types */}
                <Section title="Supported file types">
                    <div style={s.tableWrap}>
                        <table style={s.table}>
                            <thead>
                                <tr>
                                    <th style={s.th}>Extension</th>
                                    <th style={s.th}>What it is</th>
                                </tr>
                            </thead>
                            <tbody>
                                {FILE_TYPES.map(([ext, what]) => (
                                    <tr key={ext}>
                                        <td style={{ ...s.td, fontFamily: 'JetBrains Mono, ui-monospace, monospace' }}>{ext}</td>
                                        <td style={s.td}>{what}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Section>

                {/* FAQ */}
                <Section title="FAQ">
                    <div style={s.grid2}>
                        <Faq q="Will this break my machine?" a="No. Only display text inside a hardcoded list of attributes is changed, and a pre-write verifier blocks any file where numbers, ports, or non-whitelisted attributes would change. If the verifier flags an unsafe change, the file is left untouched and the issue is reported in the UI." />
                        <Faq q="Does it talk to the internet?" a="Not by default. The 542-term dictionary lives on disk. Optional DeepL auto-translation is only enabled if you supply your own API key; even then, only unknown Chinese strings are sent." />
                        <Faq q="Why does Windows SmartScreen warn me?" a="The build is unsigned. Click 'More info' → 'Run anyway.' Code-signing will come once usage settles." />
                        <Faq q="What if a Chinese phrase isn't in the dictionary?" a="It stays as-is and is listed in an 'Untranslated' report after the run. Add the term in the dictionary editor (or to translations.json) and re-translate." />
                        <Faq q="Can I edit the dictionary?" a="Yes. The Dictionary tab supports search, add, edit, delete, and CSV import/export. Save back to translations.json and the next run uses the new terms." />
                        <Faq q="Where do my files go?" a="Nowhere. Everything stays on the workshop PC. The .cfgpkg is extracted to a temp working folder next to itself; originals are copied to a BACKUP/ subfolder before any change is written." />
                    </div>
                </Section>

                <footer style={s.footer}>
                    XOVR Parts &amp; Service &mdash; Machine Config Translator v{APP_VERSION} &middot;
                    For TubePro / CypCut fiber laser tube cutters.
                </footer>
            </div>
        </div>
    );
};

/* ─────────────────── Sub-components ─────────────────── */

const Section: React.FC<{ title: string; subtitle?: string; children: React.ReactNode }> = ({ title, subtitle, children }) => (
    <section style={s.section}>
        <h2 style={s.h2}>{title}</h2>
        {subtitle && <p style={s.subtitle} dangerouslySetInnerHTML={{ __html: subtitle }} />}
        <div style={{ marginTop: 24 }}>{children}</div>
    </section>
);

const ValueCard: React.FC<{ head: string; body: string }> = ({ head, body }) => (
    <div style={s.card}>
        <div style={s.accentBar} />
        <div style={s.cardHead}>{head}</div>
        <div style={s.cardBody}>{body}</div>
    </div>
);

const Card: React.FC<{ head: string; children: React.ReactNode }> = ({ head, children }) => (
    <div style={s.card}>
        <div style={s.cardHead}>{head}</div>
        <div style={s.cardBody}>{children}</div>
    </div>
);

const Step: React.FC<{ n: number; head: string; children: React.ReactNode }> = ({ n, head, children }) => (
    <div style={s.card}>
        <div style={s.stepRow}>
            <span style={s.stepBubble}>{n}</span>
            <span style={s.cardHead}>{head}</span>
        </div>
        <div style={{ ...s.cardBody, marginTop: 12 }}>{children}</div>
    </div>
);

const Shot: React.FC<{ label: string; src: string; caption: string }> = ({ label, src, caption }) => (
    <figure style={s.shotFig}>
        <div style={s.shotLabel}>{label}</div>
        <img src={src} alt={caption} style={s.shotImg} />
        <figcaption style={s.shotCap}>{caption}</figcaption>
    </figure>
);

const Faq: React.FC<{ q: string; a: string }> = ({ q, a }) => (
    <div style={s.card}>
        <div style={s.cardHead}>{q}</div>
        <div style={s.cardBody}>{a}</div>
    </div>
);

/* ─────────────────────── Styles ───────────────────────── */
const s: Record<string, React.CSSProperties> = {
    page: {
        minHeight: '100vh',
        background: 'radial-gradient(ellipse at top, #1a1a1a 0%, #0a0a0a 100%)',
        color: '#e5e5e5',
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        padding: '40px 20px 80px',
    },
    container: { maxWidth: 1080, margin: '0 auto' },
    topBar: { marginBottom: 24 },
    backLink: {
        color: '#888',
        textDecoration: 'none',
        fontSize: 13,
    },

    /* Hero */
    hero: {
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
        gap: 40,
        alignItems: 'center',
        padding: '32px 0 56px',
        borderBottom: '1px solid #1f1f1f',
    },
    heroLeft: { minWidth: 0 },
    heroRight: { minWidth: 0 },
    eyebrow: {
        display: 'inline-block',
        padding: '4px 10px',
        borderRadius: 999,
        border: '1px solid #2a2a2a',
        background: '#141414',
        color: '#bbb',
        fontSize: 12,
        marginBottom: 16,
    },
    title: {
        fontSize: 44,
        lineHeight: 1.1,
        fontWeight: 800,
        letterSpacing: '-0.02em',
        color: '#ffffff',
        margin: 0,
    },
    lede: {
        marginTop: 16,
        color: '#bbb',
        fontSize: 17,
        lineHeight: 1.55,
        maxWidth: 560,
    },
    ctaRow: { marginTop: 28, display: 'flex', flexWrap: 'wrap', gap: 12 },
    primaryBtn: {
        display: 'inline-flex',
        alignItems: 'center',
        background: '#ff4500',
        color: '#fff',
        textDecoration: 'none',
        padding: '12px 18px',
        borderRadius: 8,
        fontWeight: 600,
        fontSize: 14,
        boxShadow: '0 6px 16px rgba(255, 69, 0, 0.25)',
    },
    secondaryBtn: {
        display: 'inline-flex',
        alignItems: 'center',
        background: '#141414',
        color: '#e5e5e5',
        textDecoration: 'none',
        padding: '12px 18px',
        borderRadius: 8,
        fontWeight: 600,
        fontSize: 14,
        border: '1px solid #2a2a2a',
    },
    metaRow: { marginTop: 12, color: '#777', fontSize: 12 },
    heroFrame: {
        background: '#141414',
        border: '1px solid #2a2a2a',
        borderRadius: 12,
        padding: 8,
    },
    heroImg: { width: '100%', height: 'auto', display: 'block', borderRadius: 8 },
    caption: { marginTop: 10, textAlign: 'center', color: '#888', fontSize: 12 },

    /* Sections */
    section: { padding: '56px 0', borderBottom: '1px solid #1f1f1f' },
    h2: { fontSize: 26, fontWeight: 700, letterSpacing: '-0.01em', color: '#fff', margin: 0 },
    subtitle: { marginTop: 8, color: '#999', fontSize: 15, maxWidth: 720 },
    body: { color: '#bbb', fontSize: 15, lineHeight: 1.6, margin: 0 },

    /* Grids */
    grid4: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: 16,
    },
    grid3: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: 20,
    },
    grid2: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
        gap: 20,
    },

    /* Cards */
    card: {
        background: '#141414',
        border: '1px solid #2a2a2a',
        borderRadius: 12,
        padding: 20,
    },
    accentBar: {
        width: 28,
        height: 3,
        borderRadius: 999,
        background: '#ff4500',
        marginBottom: 12,
    },
    cardHead: { color: '#fff', fontWeight: 600, fontSize: 15 },
    cardBody: { color: '#999', fontSize: 13.5, lineHeight: 1.55, marginTop: 8 },

    /* Step */
    stepRow: { display: 'flex', alignItems: 'center', gap: 10 },
    stepBubble: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 26,
        height: 26,
        borderRadius: 999,
        background: '#ff4500',
        color: '#fff',
        fontSize: 13,
        fontWeight: 700,
    },

    /* Shots */
    shotFig: {
        background: '#141414',
        border: '1px solid #2a2a2a',
        borderRadius: 12,
        overflow: 'hidden',
        margin: 0,
    },
    shotLabel: {
        padding: '8px 12px',
        fontSize: 11,
        letterSpacing: '0.12em',
        color: '#888',
        borderBottom: '1px solid #1f1f1f',
        background: '#0f0f0f',
    },
    shotImg: { width: '100%', height: 'auto', display: 'block' },
    shotCap: { padding: '10px 12px', fontSize: 12, color: '#888', borderTop: '1px solid #1f1f1f' },

    /* Whitelist */
    whitelistBox: {
        background: '#0f0f0f',
        border: '1px solid #2a2a2a',
        borderRadius: 12,
        padding: 20,
    },
    whitelistHead: { color: '#fff', fontWeight: 600, fontSize: 15 },
    whitelistSub: { color: '#777', fontSize: 12, marginTop: 4 },
    whitelistGrid: {
        marginTop: 14,
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
        gap: 8,
    },
    whitelistChip: {
        background: '#141414',
        border: '1px solid #2a2a2a',
        borderRadius: 6,
        padding: '6px 10px',
        color: '#ddd',
        fontSize: 12,
        fontFamily: 'JetBrains Mono, ui-monospace, monospace',
    },

    /* Bullets */
    bullets: {
        marginTop: 14,
        paddingLeft: 18,
        color: '#bbb',
        fontSize: 14,
        lineHeight: 1.6,
    },

    /* Table */
    tableWrap: {
        background: '#141414',
        border: '1px solid #2a2a2a',
        borderRadius: 12,
        overflow: 'hidden',
    },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: 14 },
    th: {
        textAlign: 'left',
        padding: '12px 16px',
        background: '#0f0f0f',
        color: '#888',
        fontSize: 11,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        borderBottom: '1px solid #2a2a2a',
        fontWeight: 600,
    },
    td: { padding: '12px 16px', color: '#ccc', borderBottom: '1px solid #1f1f1f' },

    /* Misc */
    code: {
        background: '#0f0f0f',
        border: '1px solid #2a2a2a',
        borderRadius: 4,
        padding: '1px 6px',
        fontFamily: 'JetBrains Mono, ui-monospace, monospace',
        fontSize: 13,
        color: '#e5e5e5',
    },
    inlineLink: { color: '#ff4500', textDecoration: 'none', borderBottom: '1px solid #ff4500' },
    footer: {
        marginTop: 56,
        paddingTop: 24,
        textAlign: 'center',
        color: '#555',
        fontSize: 12,
        borderTop: '1px solid #1f1f1f',
    },
};

export default MachineConfigTranslatorPage;
